/**
 * AI Clinical Surfaces Registry
 *
 * Single source of truth for every place the CaraLoom platform generates
 * AI clinical content. Drives:
 *   - The public `/ai-safety` transparency page (lists all 6 surfaces).
 *   - The `<AISafetyBadge/>` component (looks up a surface by id and
 *     renders the validation state badge, silently absent if unsigned).
 *
 * Add new surfaces here whenever a new AI clinical endpoint ships. Update
 * `clinical_validation` whenever a licensed clinician signs off (and again
 * every 6 months for the refresh).
 *
 * Risk class:
 *   - high   → output directly informs a clinical decision (risk score,
 *              prescribing route, triage urgency). Validation is required
 *              before paid acquisition.
 *   - medium → output is a clinician-edited draft (SOAP from voice/text,
 *              note summary). Validation is required, but clinician
 *              authorship is preserved (they sign the final note).
 *   - low    → output is operational/non-clinical (claim-scrub denial
 *              risk, fit-score for clinician onboarding). Validation
 *              improves quality but is not safety-critical.
 *
 * Schema for `clinical_validation` (silently absent when unsigned):
 *   {
 *     reviewer_name:   'Jane Doe, MD',
 *     credentials:     ['MD', 'FAAFP'],
 *     npi:             '1234567890',
 *     signed_at:       '2026-02-25',
 *     expires_at:      '2026-08-25',
 *     cases_reviewed:  24,
 *     pass_rate:       0.92,                // 0-1
 *     attestation_url: null,
 *   }
 */
export const AI_SURFACES = [
  {
    id: 'patient.intake_assessment',
    name: 'Patient Intake Assessment',
    surface_label: 'Onboarding triage scoring',
    endpoint: 'POST /api/auth/register (patient flow)',
    backend_fn: 'run_patient_assessment',
    risk_class: 'high',
    inputs: ['Diagnosis', 'Care level needed', 'Functional limitations', 'Mental health factors', 'Location', 'Budget', 'Schedule'],
    outputs: ['care_complexity_score (0–100)', 'urgency_level (Routine|Moderate|High Risk)', 'recommended_care_level (CNA|LPN|RN|NP|MD|Hospice MD)', 'care_type_recommendation (telehealth|in_person|both)', 'reasoning'],
    viewer: 'Patient + matched clinician',
    guardrails: ['PHI scrubbed at prompt interpolation', 'Default fallback returned on AI failure', 'No prescriptive medical advice in reasoning'],
    clinical_validation: null,
  },
  {
    id: 'clinician.intake_assessment',
    name: 'Clinician Intake Fit Scoring',
    surface_label: 'Clinician onboarding fit',
    endpoint: 'POST /api/clinician/assessment',
    backend_fn: 'run_clinician_assessment',
    risk_class: 'low',
    inputs: ['Years of experience', 'Certifications', 'Specialties', 'Preferred care setting', 'Weekly availability', 'Comfort with diagnoses'],
    outputs: ['competency_score (0–100)', 'risk_tolerance_category (Low|Medium|High)', 'care_intensity_capacity (0–100)', 'strengths', 'recommended_case_types'],
    viewer: 'Clinician (self-view) + admin',
    guardrails: ['Output never gates licensure or platform access', 'Used as a soft signal for matching, not eligibility'],
    clinical_validation: null,
  },
  {
    id: 'soap.text_appointment',
    name: 'SOAP Note · Text Appointment',
    surface_label: 'Appointment-derived SOAP draft',
    endpoint: 'POST /api/appointments/{id}/generate-soap',
    backend_fn: 'generate_soap_note',
    risk_class: 'medium',
    inputs: ['appointment.notes (clinician dictation/typing)'],
    outputs: ['subjective', 'objective', 'assessment', 'plan'],
    viewer: 'Clinician (draft view; clinician signs the final note)',
    guardrails: ['Clinician-authored draft, AI restructures only', 'AI never invents clinical facts (per system prompt)', 'PHI scrubbed before LLM', 'Clinician signature required before persistence'],
    clinical_validation: null,
  },
  {
    id: 'soap.voice_transcribe',
    name: 'SOAP Note · Voice Transcription',
    surface_label: 'Voice-to-SOAP via Whisper STT + Gemini structuring',
    endpoint: 'POST /api/voice/transcribe-to-soap',
    backend_fn: 'transcribe_voice_to_soap',
    risk_class: 'medium',
    inputs: ['Base64 audio (clinician dictation)', 'patient_id'],
    outputs: ['subjective', 'objective', 'assessment', 'plan', 'suggested_title', 'key_diagnoses', 'vitals_detected'],
    viewer: 'Clinician (draft view; clinician signs the final note)',
    guardrails: ['BAA required at runtime (_require_phi_baa gate)', 'Whisper STT covered by BAA chain', 'Transcription scrubbed before LLM structuring step', 'Vital signs flagged separately, never invented'],
    clinical_validation: null,
  },
  {
    id: 'risk.patient_stratification',
    name: 'Patient Clinical Risk Assessment',
    surface_label: 'AI risk stratification from check-ins + profile',
    endpoint: 'POST /api/risk/assess',
    backend_fn: 'assess_patient_risk',
    risk_class: 'high',
    inputs: ['Patient profile (complexity, conditions, care level, urgency)', 'Last 7 daily check-ins (mood, pain, symptoms)'],
    outputs: ['risk_level (low|moderate|high|critical)', 'risk_score (0–100)', 'risk_factors', 'recommendations', 'alert_clinician (boolean)', 'suggested_interventions'],
    viewer: 'Clinician (caring for patient) + admin',
    guardrails: ['_check_phi_access enforces care-relationship before run', 'PHI scrubbed at prompt interpolation', 'alert_clinician flag drives in-platform notification, not automated escalation', 'Audit log entry per assessment'],
    clinical_validation: null,
  },
  {
    id: 'notes.handoff_summary',
    name: 'Clinical Note Handoff Summary',
    surface_label: 'AI-generated handoff / shift-change summary',
    endpoint: 'POST /api/clinical-notes/ai-summary',
    backend_fn: 'generate_note_summary',
    risk_class: 'medium',
    inputs: ['Last 7 days of clinical notes (or explicit note_ids list)', 'summary_type (handoff|shift_change|care_plan|weekly)'],
    outputs: ['Structured summary (Current Status / Key Changes / Active Issues / Recommended Actions per summary_type)'],
    viewer: 'Receiving clinician at handoff + admin',
    guardrails: ['_check_phi_access enforces care-relationship', 'Patient name referenced as "the patient" in prompt; real name persisted only in stored doc', 'Receiving clinician must acknowledge handoff in-platform'],
    clinical_validation: null,
  },
  {
    id: 'rcm.claim_scrub',
    name: 'RCM Claim Scrub · Denial Prevention',
    surface_label: 'Pre-submission AI claim audit',
    endpoint: 'POST /api/rcm/claims/{claim_id}/scrub',
    backend_fn: 'claim_scrub',
    risk_class: 'low',
    inputs: ['Claim payload (CPT, ICD-10, modifiers, POS, units, provider NPI, etc.)'],
    outputs: ['status (clean|hold|blocked)', 'risk_score (0–100)', 'risk_rationale', 'issues[] (severity, location, rationale, suggested_fix)', 'verified_clean[]', 'recurrence_prevention'],
    viewer: 'Admin (RCM workflow); clinician sees status pill on claim row',
    guardrails: ['Output is advisory — submission gated by clinician, not AI', 'Vertex AI timeout-bounded (≤55s) to stay under Cloudflare gateway', 'Last result cached on claim for audit log', 'AI-unavailable degrades to "pending" status, never auto-clean'],
    clinical_validation: null,
  },
  {
    id: 'carelibrary.navigator',
    name: 'Care Library Navigator',
    surface_label: 'Free-text caregiver question → best 3-min clip',
    endpoint: 'POST /api/care-library/navigate',
    backend_fn: 'care_library_navigate',
    risk_class: 'low',
    inputs: ['Caregiver free-text problem statement (PHI-scrubbed)'],
    outputs: ['best_item_id (catalog row id or null)', 'reason (1–2 sentence warm intro)', 'marketplace_thread (suggested clinician question)'],
    viewer: 'Caregiver / family-side users on /care-library',
    guardrails: [
      'Input is PHI-scrubbed before LLM (scrub_phi)',
      'System prompt forbids medical advice, prescriptions, or diagnostic claims',
      'Hard emergency-keyword shortcut routes to 911 (chest pain / stroke signs / SI)',
      'Audit log entry per call (phi_audit_log)',
      'AI-unavailable degrades to a warm fallback, never silently fails',
    ],
    clinical_validation: null,
  },
];

export const AI_SURFACE_BY_ID = Object.fromEntries(AI_SURFACES.map((s) => [s.id, s]));

export default AI_SURFACES;
